import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import authService from '@/lib/auth'
import patientAuthService from '@/lib/patient-auth'
import logoImage from '/assets/logo-transparent.png'

interface UnifiedLoginProps {
  return_to?: string
}

const staffLoginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

const patientLoginSchema = z.object({
  email: z.string().min(1, 'Email or phone number is required'),
  password: z.string().min(1, 'Password is required'),
})

type StaffLoginFormData = z.infer<typeof staffLoginSchema>
type PatientLoginFormData = z.infer<typeof patientLoginSchema>

export default function UnifiedLogin({ return_to }: UnifiedLoginProps) {
  const [error, setError] = useState('')
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [activeTab, setActiveTab] = useState<'staff' | 'patient'>('staff')

  const staffForm = useForm<StaffLoginFormData>({
    resolver: zodResolver(staffLoginSchema),
  })

  const patientForm = useForm<PatientLoginFormData>({
    resolver: zodResolver(patientLoginSchema),
  })

  // Check if user is already authenticated
  useEffect(() => {
    const checkExistingAuth = () => {
      // Check staff auth
      const staffToken = authService.getToken()
      if (staffToken && !authService.isTokenExpired()) {
        const redirectUrl = return_to || '/clinician/dashboard'
        router.visit(redirectUrl, {
          replace: true,
          onError: () => {
            authService.clearTokens()
            checkPatientAuth()
          },
        })
        return
      }

      checkPatientAuth()
    }

    const checkPatientAuth = () => {
      // Check patient auth
      const patientToken = patientAuthService.getToken()
      if (patientToken) {
        const redirectUrl = return_to || '/patients/progress'
        router.visit(redirectUrl, {
          replace: true,
          onError: () => {
            patientAuthService.clearTokens()
            setIsCheckingAuth(false)
          },
        })
      } else {
        setIsCheckingAuth(false)
      }
    }

    checkExistingAuth()
  }, [return_to])

  const onStaffSubmit = async (formData: StaffLoginFormData) => {
    setError('')

    try {
      const response = await fetch('/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (response.ok) {
        authService.handleLoginResponse(data)
        // Redirect based on user role
        const isAdmin = data.user?.admin || false
        const defaultRedirect = isAdmin ? '/admin/console' : '/clinician/dashboard'
        const redirectUrl = return_to || defaultRedirect
        router.visit(redirectUrl, { replace: true })
      } else {
        setError(data.error || 'Login failed')
      }
    } catch (err) {
      setError('An error occurred during login')
    }
  }

  const onPatientSubmit = async (formData: PatientLoginFormData) => {
    setError('')

    try {
      const response = await fetch('/patients/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (response.ok) {
        patientAuthService.handleLoginResponse(data)
        const redirectUrl = return_to || '/patients/progress'
        router.visit(redirectUrl, { replace: true })
      } else {
        setError(data.error || 'Login failed')
      }
    } catch (err) {
      setError('An error occurred during login')
    }
  }

  if (isCheckingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-6">
            <img
              src={logoImage}
              alt="Breakthrough Health"
              className="h-20 w-auto"
            />
          </div>
          <CardTitle className="text-2xl">Welcome to Breakthrough Health</CardTitle>
          <CardDescription>Sign in to access your account</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'staff' | 'patient')} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="staff">I'm a Clinician</TabsTrigger>
              <TabsTrigger value="patient">I'm a Patient</TabsTrigger>
            </TabsList>

            <TabsContent value="staff" className="mt-6">
              <form onSubmit={staffForm.handleSubmit(onStaffSubmit)} className="space-y-4">
                {error && activeTab === 'staff' && (
                  <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="staff-email">Email</Label>
                  <Input
                    id="staff-email"
                    type="email"
                    placeholder="clinician@example.com"
                    {...staffForm.register('email')}
                  />
                  {staffForm.formState.errors.email && (
                    <p className="text-sm text-destructive">{staffForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="staff-password">Password</Label>
                  <Input
                    id="staff-password"
                    type="password"
                    placeholder="••••••••"
                    {...staffForm.register('password')}
                  />
                  {staffForm.formState.errors.password && (
                    <p className="text-sm text-destructive">{staffForm.formState.errors.password.message}</p>
                  )}
                </div>

                <Button type="submit" className="w-full" disabled={staffForm.formState.isSubmitting}>
                  {staffForm.formState.isSubmitting ? 'Signing in...' : 'Sign In as Clinician'}
                </Button>

                <div className="text-center text-sm">
                  <a href="/password/forgot" className="text-muted-foreground hover:text-primary">
                    Forgot your password?
                  </a>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="patient" className="mt-6">
              <form onSubmit={patientForm.handleSubmit(onPatientSubmit)} className="space-y-4">
                {error && activeTab === 'patient' && (
                  <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="patient-email">Email or Phone Number</Label>
                  <Input
                    id="patient-email"
                    type="text"
                    placeholder="patient@example.com or +1234567890"
                    {...patientForm.register('email')}
                  />
                  {patientForm.formState.errors.email && (
                    <p className="text-sm text-destructive">{patientForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="patient-password">Password</Label>
                  <Input
                    id="patient-password"
                    type="password"
                    placeholder="••••••••"
                    {...patientForm.register('password')}
                  />
                  {patientForm.formState.errors.password && (
                    <p className="text-sm text-destructive">{patientForm.formState.errors.password.message}</p>
                  )}
                </div>

                <Button type="submit" className="w-full" disabled={patientForm.formState.isSubmitting}>
                  {patientForm.formState.isSubmitting ? 'Signing in...' : 'Sign In as Patient'}
                </Button>

                <div className="mt-4 rounded-md bg-blue-50 p-3 text-sm text-blue-900">
                  <p className="font-medium">For emergencies, call 911</p>
                  <p className="mt-1 text-xs">This portal is for viewing your care progress only.</p>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}
